import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Download,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Share,
  Maximize,
  SkipBack,
  SkipForward,
} from "lucide-react";
import logger from '../../../../utils/logger';
import ReactPlayer from "react-player";
import { toast } from "react-toastify";

const RecordingModal = ({ isOpen, onClose, recording }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [showSharePopup, setShowSharePopup] = useState(false);

  const playerRef = useRef(null);
  const videoContainerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        setIsPlaying((p) => !p);
      }
      if (e.code === "ArrowRight" && playerRef.current) {
        e.preventDefault();
        const newTime = Math.min(1, played + 0.05);
        playerRef.current.seekTo(newTime);
      }
      if (e.code === "ArrowLeft" && playerRef.current) {
        e.preventDefault();
        const newTime = Math.max(0, played - 0.05);
        playerRef.current.seekTo(newTime);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, played]);

  if (!isOpen || !recording) return null;
  if (typeof document === "undefined") return null;

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleProgress = (state) => {
    if (!seeking) {
      setPlayed(state.played);
    }
  };

  const handleSeekMouseDown = () => {
    setSeeking(true);
  };

  const handleSeekChange = (e) => {
    setPlayed(parseFloat(e.target.value));
  };

  const handleSeekMouseUp = (e) => {
    setSeeking(false);
    if (playerRef.current) {
      playerRef.current.seekTo(parseFloat(e.target.value));
    }
  };

  const handleSeekTouchEnd = (e) => {
    setSeeking(false);
    if (playerRef.current) {
      playerRef.current.seekTo(parseFloat(e.target.value));
    }
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  const formatTime = (seconds) => {
    const date = new Date(seconds * 1000);
    const hh = date.getUTCHours();
    const mm = date.getUTCMinutes();
    const ss = date.getUTCSeconds().toString().padStart(2, "0");
    if (hh) {
      return `${hh}:${mm.toString().padStart(2, "0")}:${ss}`;
    }
    return `${mm}:${ss}`;
  };

  const handleDownload = () => {
    logger("log", "Download recording:", recording.id);
    // Implement download functionality
    const link = document.createElement("a");
    link.href = recording.file.fileUrl;
    link.download = recording.title || "recording.mp4";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      `${process.env.REACT_APP_RECORDING_SHARE_LINK}/${recording.id}`
    );
    setShowSharePopup(false);
    toast.success("Link Copied");
  };

  const handleSkipBackward = () => {
    if (playerRef.current) {
      const newTime = Math.max(0, played * duration - 10);
      playerRef.current.seekTo(newTime / duration);
    }
  };

  const handleSkipForward = () => {
    if (playerRef.current) {
      const newTime = Math.min(duration, played * duration + 10);
      playerRef.current.seekTo(newTime / duration);
    }
  };

  const handleFullscreen = () => {
    const el = videoContainerRef.current;
    if (!el) return;
    
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen();
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen();
    }
  };

  // Handle touch events for mobile
  const handleTouchStart = (e) => {
    // Prevent default to avoid scrolling while interacting with video
    if (e.target.closest('.aspect-video')) {
      e.preventDefault();
    }
  };

  const modal = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-0 md:p-4"
      onTouchStart={handleTouchStart}
    >
      <div className="bg-black rounded-none md:rounded-lg shadow-2xl w-full h-full md:h-auto md:max-w-6xl md:mx-4 md:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex flex-col p-3 md:p-4 flex-shrink-0 relative z-10">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0 pr-2">
              <h2 className="text-lg md:text-xl font-semibold text-white truncate">
                {recording.name || "Untitled Recording"}
              </h2>
              <p className="text-xs md:text-sm text-gray-200 mt-1">
                {recording.createdAt
                  ? new Date(recording.createdAt).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
            <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
              <button
                onClick={handleDownload}
                className="p-2 md:p-2.5 hover:bg-gray-800 active:bg-gray-700 rounded-lg transition-colors touch-manipulation"
                title="Download"
              >
                <Download className="h-5 w-5 md:h-5 md:w-5 text-white" />
              </button>

              <button
                onClick={() => setShowSharePopup(!showSharePopup)}
                className="p-2 md:p-2.5 hover:bg-gray-800 active:bg-gray-700 rounded-lg transition-colors touch-manipulation relative"
                title="Share"
              >
                <Share className="h-5 w-5 md:h-5 md:w-5 text-white" />
                {showSharePopup && (
                  <div className="absolute top-12 right-0 bg-gray-800 border border-gray-700 p-4 rounded-lg shadow-xl z-20 min-w-[280px]">
                    <p className="text-white text-sm mb-2 font-medium">
                      Share Recording
                    </p>
                    <input
                      type="text"
                      value={`${process.env.REACT_APP_RECORDING_SHARE_LINK}/${recording.file.id}`}
                      readOnly
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      onClick={handleCopyLink}
                      className="w-full px-4 py-2 bg-[#7700FF] text-white rounded-lg hover:bg-[#6600DD] active:bg-[#5500BB] transition-colors font-medium touch-manipulation"
                    >
                      Copy Link
                    </button>
                  </div>
                )}
              </button>

              <button
                onClick={onClose}
                className="p-2 md:p-2.5 hover:bg-gray-800 active:bg-gray-700 rounded-lg transition-colors touch-manipulation"
                title="Close"
              >
                <X className="h-5 w-5 md:h-5 md:w-5 text-white hover:text-gray-300" />
              </button>
            </div>
          </div>
        </div>

        {/* Video Player */}
        <div className="relative bg-black flex-1 flex items-center justify-center min-h-0">
          <div
            ref={videoContainerRef}
            className="aspect-video w-full cursor-pointer touch-none"
            onClick={handlePlayPause}
            onTouchStart={(e) => {
              // Allow touch events for controls, but prevent default on video area
              if (!e.target.closest('.video-controls')) {
                e.stopPropagation();
              }
            }}
          >
            <ReactPlayer
              ref={playerRef}
              url={recording.file.fileUrl}
              width="100%"
              height="100%"
              playing={isPlaying}
              volume={isMuted ? 0 : volume}
              playbackRate={playbackRate}
              onProgress={handleProgress}
              onDuration={handleDuration}
              controls={false}
              config={{
                file: {
                  attributes: {
                    controlsList: "nodownload",
                  },
                },
              }}
            />
          </div>

          {/* Custom Controls Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-3 md:p-4 video-controls">
            <div className="mb-3 md:mb-4">
              <input
                type="range"
                min="0"
                max="1"
                step="any"
                value={played}
                onMouseDown={handleSeekMouseDown}
                onTouchStart={handleSeekMouseDown}
                onChange={handleSeekChange}
                onMouseUp={handleSeekMouseUp}
                onTouchEnd={handleSeekTouchEnd}
                className="w-full h-2 md:h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer touch-manipulation"
                style={{
                  background: `linear-gradient(to right, #7700FF 0%, #7700FF ${
                    played * 100
                  }%, #4B5563 ${played * 100}%, #4B5563 100%)`,
                }}
              />
            </div>

            <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-0">
              <div className="flex items-center space-x-2 md:space-x-4 w-full md:w-auto justify-center md:justify-start">
                <button
                  onClick={handleSkipBackward}
                  className="p-2.5 md:p-2 hover:bg-white/20 active:bg-white/30 rounded-full transition-colors touch-manipulation"
                  title="Rewind 10 seconds"
                >
                  <SkipBack className="h-6 w-6 md:h-5 md:w-5 text-white" />
                </button>

                <button
                  onClick={handlePlayPause}
                  className="p-3 md:p-2 hover:bg-white/20 active:bg-white/30 rounded-full transition-colors touch-manipulation bg-white/10 backdrop-blur-sm"
                >
                  {isPlaying ? (
                    <Pause className="h-7 w-7 md:h-6 md:w-6 text-white" />
                  ) : (
                    <Play className="h-7 w-7 md:h-6 md:w-6 text-white ml-0.5" />
                  )}
                </button>

                <button
                  onClick={handleSkipForward}
                  className="p-2.5 md:p-2 hover:bg-white/20 active:bg-white/30 rounded-full transition-colors touch-manipulation"
                  title="Forward 10 seconds"
                >
                  <SkipForward className="h-6 w-6 md:h-5 md:w-5 text-white" />
                </button>

                <div className="text-white text-sm md:text-sm font-mono px-2 md:px-4">
                  {formatTime(played * duration)} / {formatTime(duration)}
                </div>
              </div>

              <div className="flex items-center space-x-2 md:space-x-4 w-full md:w-auto justify-center md:justify-end">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleMute}
                    className="p-2 md:p-1 hover:bg-white/20 active:bg-white/30 rounded transition-colors touch-manipulation"
                    title={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? (
                      <VolumeX className="h-5 w-5 text-white" />
                    ) : (
                      <Volume2 className="h-5 w-5 text-white" />
                    )}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-16 md:w-20 h-2 md:h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer touch-manipulation"
                    style={{
                      background: `linear-gradient(to right, #7700FF 0%, #7700FF ${
                        (isMuted ? 0 : volume) * 100
                      }%, #4B5563 ${(isMuted ? 0 : volume) * 100}%, #4B5563 100%)`,
                    }}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-white text-xs md:text-sm hidden md:inline">Speed:</span>
                  <select
                    value={playbackRate}
                    onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                    className="bg-gray-800/90 border border-gray-600 text-white text-xs md:text-sm rounded-lg px-2 md:px-3 py-1.5 md:py-1 focus:outline-none focus:ring-2 focus:ring-purple-500 hover:bg-gray-700 transition-colors cursor-pointer touch-manipulation"
                  >
                    <option value={0.5}>0.5x</option>
                    <option value={0.75}>0.75x</option>
                    <option value={1.0}>1.0x</option>
                    <option value={1.25}>1.25x</option>
                    <option value={1.5}>1.5x</option>
                    <option value={2.0}>2.0x</option>
                  </select>
                </div>

                <button
                  onClick={handleFullscreen}
                  className="p-2 hover:bg-white/20 active:bg-white/30 rounded-lg transition-colors touch-manipulation"
                  title="Fullscreen"
                >
                  <Maximize className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Recording Info */}
        <div className="p-4 md:p-6 bg-gray-800/50 border-t border-gray-700 flex-shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div>
              <h3 className="text-xs md:text-sm font-medium text-gray-400 mb-1 uppercase tracking-wide">
                Duration
              </h3>
              <p className="text-sm md:text-base text-white font-semibold">{formatTime(duration)}</p>
            </div>
            <div>
              <h3 className="text-xs md:text-sm font-medium text-gray-400 mb-1 uppercase tracking-wide">File Size</h3>
              <p className="text-sm md:text-base text-white font-semibold">
                {recording.file.size
                  ? `${(recording.file.size / (1024 * 1024)).toFixed(2)} MB`
                  : "0 MB"}
              </p>
            </div>
            <div>
              <h3 className="text-xs md:text-sm font-medium text-gray-400 mb-1 uppercase tracking-wide">Format</h3>
              <p className="text-sm md:text-base text-white font-semibold">
                {recording.format || "MP4"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default RecordingModal;
